import { useEffect, useRef, useState } from "react";
import { register, call, remove } from "../funcStore";
import { searchVtubers } from "../api/mockApi";
import { logAction } from "../api/logApi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VTuberCard from "../components/VTuberCard";
import FilterGroup from "../components/FilterGroup";
import ActiveFilterChips from "../components/ActiveFilterChips";
import { FILTER_GROUPS, FILTER_REV } from "../data/filterGroups";
import { getActiveFilters } from "../utils/searchFilters";
import { NameInput } from "../components/NameInput";

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },
  searchMain: {
    flex: 1,
    display: "flex",
    gap: "24px",
    padding: "24px",
    alignItems: "flex-start",
  },
  filterPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "240px",
    flexShrink: 0,
    background: "#fff",
    padding: "16px",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  toggleField: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.85rem",
  },
  submitButton: {
    marginTop: "8px",
    padding: "10px",
    border: "none",
    borderRadius: "4px",
    background: "#4b3f8f",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: "default",
  },
  resultList: {
    flex: 1,
  },
  resultGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "16px",
  },
  resultSummary: {
    margin: "0 0 12px",
    fontSize: "0.85rem",
    color: "#555",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginTop: "20px",
  },
  pageButton: {
    padding: "6px 14px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    background: "#fff",
    cursor: "pointer",
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: "default",
  },
};

const PAGE_SIZE = 12;

const STATE_BEFORE_INIT = "beforeInit";
const STATE_IDLE = "idle";
const STATE_SEARCHING = "searching";
const STATE_RESULTS_SHOWN = "resultsShown";
const STATE_NO_RESULTS = "noResults";

function SearchPage() {
  const [state, setState] = useState(STATE_BEFORE_INIT);

  const stateRef = useRef(state);
  stateRef.current = state;

  const [includeUnapproved, setIncludeUnapproved] = useState(false);
  const [page, setPage] = useState(1);
  const dataRef = useRef({
    filters: {},
    results: [],
    lastLoggedFilters: null,
  });

  const init = () => {
    register("searchPage.getFilter", (groupKey) => {
      return dataRef.current.filters[groupKey];
    });

    register("searchPage.setFilter", (groupKey, key, value) => {
      dataRef.current.filters[groupKey][key] = value ?? "";
      call(`filterGroup.${groupKey}.update`);
      call(`filterGroup.${groupKey}.open`);
    });

    register("searchPage.setFilterName", (value) => {
      dataRef.current.filters["vtuberName"]["vtuberName"] = value ?? "";
    });

    register("searchPage.setIncludeUnapproved", (value) => {
      dataRef.current.filters["options"]["includeUnapproved"] = !!value;
    });

    register("searchPage.setFilterByTag", (tag) => {
      const { groupKey, key } = FILTER_REV[tag];
      call("searchPage.setFilter", groupKey, key, tag);
    });

    register("searchPage.submit", async () => {
      if (stateRef.current === STATE_SEARCHING) {
        return;
      }

      setState(STATE_SEARCHING);

      const appliedFilters = Object.assign(
        {},
        ...Object.values(dataRef.current.filters),
      );

      // FilterGroupの初期マウント時にも呼ばれるため、条件に変化がない再送信はログしない
      const filtersKey = JSON.stringify(appliedFilters);
      if (filtersKey !== dataRef.current.lastLoggedFilters) {
        dataRef.current.lastLoggedFilters = filtersKey;
        logAction("search_submit", { filters: appliedFilters });
      }

      const results = await searchVtubers(appliedFilters);
      console.log("SearchPageDebug", results, appliedFilters);
      dataRef.current.results = results;
      setPage(1);

      setState(results.length > 0 ? STATE_RESULTS_SHOWN : STATE_NO_RESULTS);
    });

    register("searchPage.getFilterGroup", (groupKey) =>
      FILTER_GROUPS.find((group) => group.key === groupKey),
    );

    initFilters();
    setState(STATE_IDLE);
  };

  function initFilters() {
    const allFilters = {
      vtuberName: { vtuberName: "" },
      options: { includeUnapproved: false },
    };
    FILTER_GROUPS.forEach((filter) => {
      const groupFilters = {};
      filter.filters.forEach((f) => {
        groupFilters[f.key] = "";
      });
      allFilters[filter.key] = groupFilters;
    });
    dataRef.current.filters = allFilters;
  }

  useEffect(() => {
    if (state === STATE_BEFORE_INIT) {
      init();
    }
    if (state === STATE_IDLE) {
      //詳細ページでタグをクリックしていた場合
      const detailTag = call("vtuberProfile.getClickTag");
      remove("vtuberProfile.getClickTag");
      if (detailTag) {
        call("searchPage.setFilterByTag", detailTag);
        call("searchPage.submit");
        return;
      }
      const homeTag = call("app.getClickedTag");
      if (homeTag) {
        call("searchPage.setFilterByTag", homeTag);
        call("searchPage.submit");
        return;
      }
      call("searchPage.submit");
    }
  }, [state]);

  useEffect(() => {
    return () => {
      remove("searchPage.setFilter");
      remove("searchPage.submit");
      remove("searchPage.getFilterGroup");
    };
  }, []);

  if (state === STATE_BEFORE_INIT) {
    return null;
  }

  const results = dataRef.current.results;
  const totalCount = results.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedResults = results.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const activeFilters = getActiveFilters(
    dataRef.current.filters,
    FILTER_GROUPS,
  );

  const handleClearFilter = (groupKey, filterKey) => {
    if (groupKey === "vtuberName") {
      call("searchPage.setFilterName", "");
      call("NameInputClearName","")
    } else {
      call("searchPage.setFilter", groupKey, filterKey, "");
    }
    call("searchPage.submit");
  };

  const handleIncludeUnapproved = (e) => {
    setIncludeUnapproved(e.target.checked);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const nameData = call("NameInputGatValue")
    call("searchPage.setFilterName", nameData);
    call("searchPage.setIncludeUnapproved", includeUnapproved);
    call("searchPage.submit");
  };

  const handlePrevPage = () => {
    setPage((current) => Math.max(1, current - 1));
  };

  const handleNextPage = () => {
    setPage((current) => Math.min(totalPages, current + 1));
  };

  return (
    <div style={styles.page}>
      <Header />

      <main style={styles.searchMain}>
        <form style={styles.filterPanel} onSubmit={handleSubmit}>
          <NameInput />

          {FILTER_GROUPS.map((group) => (
            <FilterGroup key={group.key} groupKey={group.key} />
          ))}

          <label style={styles.toggleField}>
            <input
              type="checkbox"
              checked={includeUnapproved}
              onChange={handleIncludeUnapproved}
            />
            未承認の属性も検索対象に含める
          </label>

          <button
            type="submit"
            style={{
              ...styles.submitButton,
              ...(state === STATE_SEARCHING ? styles.submitButtonDisabled : {}),
            }}
            disabled={state === STATE_SEARCHING}
          >
            {state === STATE_SEARCHING ? "検索中..." : "検索"}
          </button>
        </form>

        <section style={styles.resultList}>
          <ActiveFilterChips
            items={activeFilters}
            onClear={handleClearFilter}
          />

          {state === STATE_SEARCHING && <p>検索中...</p>}

          {state === STATE_NO_RESULTS && (
            <p>条件に一致するVTuberが見つかりませんでした。</p>
          )}

          {state === STATE_RESULTS_SHOWN && (
            <>
              <p style={styles.resultSummary}>検索結果 {totalCount}件</p>

              <div style={styles.resultGrid}>
                {pagedResults.map((vtuber) => (
                  <VTuberCard key={vtuber.id} vtuberId={vtuber.id} />
                ))}
              </div>

              {totalPages > 1 && (
                <div style={styles.pagination}>
                  <button
                    type="button"
                    style={{
                      ...styles.pageButton,
                      ...(currentPage === 1 ? styles.pageButtonDisabled : {}),
                    }}
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  >
                    前へ
                  </button>

                  <span>
                    {currentPage} / {totalPages}
                  </span>

                  <button
                    type="button"
                    style={{
                      ...styles.pageButton,
                      ...(currentPage === totalPages
                        ? styles.pageButtonDisabled
                        : {}),
                    }}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    次へ
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default SearchPage;
